import { FormEvent, useEffect, useState } from 'react';
import type { ShippingRate, ShippingZone, FreeShippingCampaign } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { formatEur } from '../../lib/format';
import { useToast } from '../../components/admin/Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';
const thRight = `${thClass} text-right`;

export default function AdminShippingPage(): JSX.Element {
  const toast = useToast();
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [rates, setRates] = useState<ShippingRate[]>([]);
  const [campaigns, setCampaigns] = useState<FreeShippingCampaign[]>([]);

  const [zoneName, setZoneName] = useState('');
  const [zonePrefixes, setZonePrefixes] = useState('');

  const [rateZoneId, setRateZoneId] = useState<string>('');
  const [rateName, setRateName] = useState('');
  const [rateMax, setRateMax] = useState(2000);
  const [ratePrice, setRatePrice] = useState(3.95);
  const [rateDays, setRateDays] = useState('');

  const load = async (): Promise<void> => {
    const [z, r, c] = await Promise.all([
      api.get<ShippingZone[]>('/admin/shipping/zones'),
      api.get<ShippingRate[]>('/admin/shipping/rates'),
      api.get<FreeShippingCampaign[]>('/admin/shipping/campaigns'),
    ]);
    setZones(z.data);
    setRates(r.data);
    setCampaigns(c.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const createZone = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post('/admin/shipping/zones', {
        name: zoneName,
        postalPrefixes: zonePrefixes.split(',').map((s) => s.trim()).filter(Boolean),
        isActive: true,
      });
      setZoneName('');
      setZonePrefixes('');
      toast.success('Zona criada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar zona'));
    }
  };

  const createRate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post('/admin/shipping/rates', {
        zoneId: rateZoneId,
        name: rateName,
        minWeightG: 0,
        maxWeightG: rateMax,
        price: ratePrice,
        estimatedDays: rateDays || null,
        isActive: true,
      });
      setRateName('');
      setRateDays('');
      toast.success('Tarifa criada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar tarifa'));
    }
  };

  return (
    <>
      <SEO title="Envios (admin)" noindex />
      <h1>Zonas, tarifas e campanhas</h1>

      <section className="mt-6">
        <h2 className="mb-3">Zonas</h2>
        <form onSubmit={createZone} className="mb-3 flex flex-wrap gap-2">
          <input
            placeholder="Nome"
            value={zoneName}
            onChange={(e) => setZoneName(e.target.value)}
            required
            className={inputClass}
          />
          <input
            placeholder="Prefixos postais (ex: 1,2,3)"
            value={zonePrefixes}
            onChange={(e) => setZonePrefixes(e.target.value)}
            required
            className={`${inputClass} flex-1 min-w-[240px]`}
          />
          <button type="submit" className={primaryBtn}>
            Criar zona
          </button>
        </form>
        <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
          {zones.map((z) => (
            <li
              key={z.id}
              className="border-b border-admin-border p-3 last:border-b-0"
            >
              <span className="font-medium">{z.name}</span>{' '}
              <span className="text-admin-text-soft">
                — {z.postalPrefixes.join(', ')}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="mb-3">Tarifas</h2>
        <form onSubmit={createRate} className="mb-3 flex flex-wrap gap-2">
          <select
            value={rateZoneId}
            onChange={(e) => setRateZoneId(e.target.value)}
            required
            className={inputClass}
          >
            <option value="">Zona…</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>
          <input
            placeholder="Nome"
            value={rateName}
            onChange={(e) => setRateName(e.target.value)}
            required
            className={inputClass}
          />
          <input
            type="number"
            placeholder="Peso máx (g)"
            value={rateMax}
            onChange={(e) => setRateMax(Number(e.target.value))}
            className={inputClass}
          />
          <input
            type="number"
            step="0.01"
            placeholder="Preço"
            value={ratePrice}
            onChange={(e) => setRatePrice(Number(e.target.value))}
            className={inputClass}
          />
          <input
            placeholder="Prazo"
            value={rateDays}
            onChange={(e) => setRateDays(e.target.value)}
            className={inputClass}
          />
          <button type="submit" className={primaryBtn}>
            Criar tarifa
          </button>
        </form>
        <table className="overflow-hidden rounded-lg bg-admin-surface shadow-sm">
          <thead>
            <tr className="border-b border-admin-border">
              <th className={thClass}>Zona</th>
              <th className={thClass}>Nome</th>
              <th className={thRight}>Peso máx</th>
              <th className={thRight}>Preço</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr
                key={r.id}
                className="border-b border-admin-border last:border-b-0"
              >
                <td className="p-3">
                  {zones.find((z) => z.id === r.zoneId)?.name ?? '—'}
                </td>
                <td className="p-3">{r.name}</td>
                <td className="p-3 text-right">{r.maxWeightG} g</td>
                <td className="p-3 text-right">{formatEur(r.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-8">
        <h2 className="mb-3">Campanhas de portes grátis</h2>
        <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
          {campaigns.map((c) => (
            <li
              key={c.id}
              className="border-b border-admin-border p-3 last:border-b-0"
            >
              <span className="font-medium">{c.name}</span>{' '}
              <span className="text-admin-text-soft">
                — {c.startsAt} até {c.endsAt}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
